// (c) chaehongee. Confidential and proprietary.

import { buildApiUrl, http } from './http'
import type {
    LoraResponse,
    LoraShareRequest,
    LoraUpdateRequest,
    LoraUnshareRequest,
    LoraCollaboratorsResponse,
} from 'models-ts/lora'

export type TrainingGender = 'female' | 'male'

export interface ListLorasParams {
    category?: string
    page?: number
    page_size?: number
}

export async function listLoras(params?: ListLorasParams): Promise<LoraResponse[]> {
    const url = new URL(buildApiUrl('/loras/'))
    if (params?.category) url.searchParams.set('category', params.category)
    if (params?.page) url.searchParams.set('page', String(params.page))
    if (params?.page_size) url.searchParams.set('page_size', String(params.page_size))
    return await http<LoraResponse[]>(url.toString(), { method: 'GET' })
}

export async function createTrainingJob(
    name: string,
    file: Blob | File,
    gender: TrainingGender = 'female'
): Promise<LoraResponse> {
    const url = buildApiUrl('/loras/training')
    const formData = new FormData()
    formData.append('name', name)
    formData.append('file', file, 'training_data.zip')
    formData.append('gender', gender)

    return await http<LoraResponse>(url, {
        method: 'POST',
        body: formData,
        timeoutMs: 300000, // 5 minutes
    })
}

export async function deleteLora(loraId: string): Promise<void> {
    const url = buildApiUrl(`/loras/${encodeURIComponent(loraId)}`)
    await http<void>(url, { method: 'DELETE' })
}

export async function shareLora(loraId: string, email: string): Promise<LoraResponse> {
    const url = buildApiUrl(`/loras/${encodeURIComponent(loraId)}/share`)
    const body: LoraShareRequest = { email }
    return await http<LoraResponse>(url, {
        method: 'POST',
        body: JSON.stringify(body),
    })
}

export async function unshareLora(loraId: string, userId: string): Promise<LoraResponse> {
    const url = buildApiUrl(`/loras/${encodeURIComponent(loraId)}/unshare`)
    const body: LoraUnshareRequest = { user_id: userId }
    return await http<LoraResponse>(url, {
        method: 'POST',
        body: JSON.stringify(body),
    })
}

export async function getLoraCollaborators(
    loraId: string
): Promise<LoraCollaboratorsResponse> {
    const url = buildApiUrl(`/loras/${encodeURIComponent(loraId)}/collaborators`)
    return await http<LoraCollaboratorsResponse>(url, { method: 'GET' })
}

export async function updateLoraPrivacy(
    loraId: string,
    privacy: 'private' | 'public'
): Promise<LoraResponse> {
    const url = buildApiUrl(`/loras/${encodeURIComponent(loraId)}`)
    const body: LoraUpdateRequest = { privacy }
    return await http<LoraResponse>(url, {
        method: 'PATCH',
        body: JSON.stringify(body),
    })
}
